(() => {
  if (window.AWP2CloudExportBridge?.version) return;
  const KEYS = {
    replicas: 'aw_garage_replicas_v1',
    active: 'aw_garage_active_replica_v1',
    timeline: 'aw_upgrade_timeline_v1',
    buildLinks: 'aw_build_replica_links_v1',
    builds: 'aw_airsoft_saved_configurations'
  };
  const NON_KNOWLEDGE_EVENT_TYPES = new Set(['certification_review_required']);
  const parse = (key, fallback) => {
    try { return JSON.parse(localStorage.getItem(key) || 'null') ?? fallback; }
    catch (_) { return fallback; }
  };
  const write = (key, value) => {
    try { localStorage.setItem(key, JSON.stringify(value)); return true; }
    catch (_) { return false; }
  };
  const state = () => window.AWGarageP2?.getState?.() || {
    replicas: parse(KEYS.replicas, []),
    timeline: parse(KEYS.timeline, []),
    buildLinks: parse(KEYS.buildLinks, {}),
    builds: parse(KEYS.builds, [])
  };
  const activeSnapshot = () => {
    const id = String(parse(KEYS.active, '') || '').trim();
    const savedReplicas = parse(KEYS.replicas, []);
    const replicas = Array.isArray(savedReplicas) ? savedReplicas : [];
    const replica = id ? replicas.find((item) => String(item?.id || '') === id) : null;
    const valid = Boolean(replica && String(replica.platform || '').trim());
    return {
      version: 1,
      activeReplicaId: valid ? id : '',
      source: valid ? 'garage-explicit' : 'none'
    };
  };

  const buildKnowledgeSignals = (current = state()) => {
    const replicas = Array.isArray(current?.replicas) ? current.replicas : [];
    const timeline = Array.isArray(current?.timeline) ? current.timeline : [];
    const builds = Array.isArray(current?.builds) ? current.builds : [];
    const links = current?.buildLinks && typeof current.buildLinks === 'object' ? current.buildLinks : {};
    const replicaById = new Map(replicas.map((item) => [String(item?.id || ''), item]));
    const signals = new Map();
    const add = (signal) => {
      if (!signal?.sourceRef || !signal?.modelHandle) return;
      signals.set(signal.sourceRef, signal);
    };

    replicas.forEach((replica) => {
      const replicaId = String(replica?.id || '').trim();
      const modelHandle = String(replica?.modelHandle || '').trim();
      if (!replicaId || !modelHandle) return;
      add({
        sourceRef: `garage-replica:${replicaId}`,
        kind: 'garage_model_presence',
        modelHandle,
        occurredAt: String(replica?.acquiredAt || replica?.createdAt || replica?.updatedAt || '').slice(0, 30) || null,
        evidenceClass: 'self_declared_presence'
      });
    });

    timeline.forEach((event) => {
      if (event?.active === false) return;
      const eventId = String(event?.id || '').trim();
      const replicaId = String(event?.replicaId || '').trim();
      const replica = replicaById.get(replicaId);
      const modelHandle = String(replica?.modelHandle || '').trim();
      if (!eventId || !modelHandle) return;
      const eventType = String(event?.type || 'note').slice(0, 60);
      if (NON_KNOWLEDGE_EVENT_TYPES.has(eventType)) return;
      const occurredAt = String(event?.date || event?.createdAt || '').slice(0, 30) || null;
      const isWorkshop = eventType === 'certification' || ['workshop', 'shopify', 'shopify_order'].includes(String(event?.source || ''));
      const certificationId = String(event?.certificationId || '').trim();
      if (isWorkshop && certificationId) {
        add({
          sourceRef: `atelier-cert:${certificationId}`,
          kind: 'workshop_certification',
          modelHandle,
          eventType,
          occurredAt,
          evidenceClass: 'workshop_candidate'
        });
        return;
      }
      add({
        sourceRef: `garage-event:${eventId}`,
        kind: 'garage_timeline',
        modelHandle,
        eventType,
        occurredAt,
        evidenceClass: isWorkshop ? 'workshop_unresolved' : 'self_declared'
      });
    });

    builds.forEach((build) => {
      const buildId = String(build?.id || '').trim();
      const replicaId = String(links?.[buildId] || '').trim();
      const replica = replicaById.get(replicaId);
      const modelHandle = String(replica?.modelHandle || '').trim();
      if (!buildId || !modelHandle) return;
      add({
        sourceRef: `garage-build:${buildId}`,
        kind: 'garage_build_context',
        modelHandle,
        occurredAt: String(build?.updatedAt || build?.createdAt || '').slice(0, 30) || null,
        evidenceClass: 'context_only'
      });
    });

    return [...signals.values()];
  };

  const envelope = () => {
    try { window.AWReplicaDossierCertificationGuardP2?.reconcile?.(); } catch (_) {}
    enrichKnowledgeRefs();
    const current = state();
    return {
      format: 'aw-p2-cloud-migration',
      schemaVersion: 3,
      source: 'online-store-local',
      exportedAt: new Date().toISOString(),
      cloudState: {
        version: 3,
        schemaVersion: 3,
        ...current,
        personalContext: activeSnapshot(),
        knowledgeSignals: buildKnowledgeSignals(current),
        migratedFrom: 'p2-local-v1',
        updatedAt: new Date().toISOString()
      }
    };
  };

  let pendingReplicaSubmit = null;
  const captureReplicaSubmit = (event) => {
    const form = event.target?.closest?.('[data-aw-garage-replica-form]');
    if (!form) return;
    pendingReplicaSubmit = {
      exactModelHandle: String(form.dataset.awExactModelHandle || '').trim(),
      editingId: String(form.dataset.editingId || '').trim()
    };
  };
  const persistPendingExactModel = () => {
    if (!pendingReplicaSubmit) return false;
    const pending = pendingReplicaSubmit;
    pendingReplicaSubmit = null;
    if (!pending.exactModelHandle) return false;
    const replicas = parse(KEYS.replicas, []);
    if (!Array.isArray(replicas) || !replicas.length) return false;
    const index = pending.editingId
      ? replicas.findIndex((item) => String(item?.id || '') === pending.editingId)
      : 0;
    if (index < 0) return false;
    if (replicas[index]?.modelHandle === pending.exactModelHandle) return false;
    replicas[index] = { ...replicas[index], modelHandle: pending.exactModelHandle };
    const ok = write(KEYS.replicas, replicas);
    if (ok) window.AWAnalytics?.track?.('p2_garage_exact_model_persisted', { model_handle: pending.exactModelHandle });
    return ok;
  };
  const restoreIncomingModelHandles = (payload = {}) => {
    const source = payload?.cloudState && typeof payload.cloudState === 'object' ? payload.cloudState : payload;
    const incoming = Array.isArray(source?.replicas) ? source.replicas : [];
    const byId = new Map(incoming
      .filter((item) => item?.id && item?.modelHandle)
      .map((item) => [String(item.id), String(item.modelHandle)]));
    if (!byId.size) return false;
    const replicas = parse(KEYS.replicas, []);
    if (!Array.isArray(replicas) || !replicas.length) return false;
    let changed = false;
    const next = replicas.map((item) => {
      const modelHandle = byId.get(String(item?.id || ''));
      if (!modelHandle || item?.modelHandle === modelHandle) return item;
      changed = true;
      return { ...item, modelHandle };
    });
    return changed ? write(KEYS.replicas, next) : false;
  };
  const repairCertificationIds = (hint = {}) => {
    const replicas = parse(KEYS.replicas, []);
    const timeline = parse(KEYS.timeline, []);
    if (!Array.isArray(replicas) || !Array.isArray(timeline) || !timeline.length) return false;
    const replicaMap = new Map(replicas.map((item) => [String(item?.id || ''), item]));
    const hintedId = String(hint.certificationId || '').trim();
    const hintedReplicaId = String(hint.replicaId || '').trim();
    const hintedDate = String(hint.certifiedAt || '').slice(0, 10);
    let changed = false;
    const next = timeline.map((event) => {
      if (event?.type !== 'certification' || event?.certificationId) return event;
      const replicaId = String(event?.replicaId || '');
      if (hintedId && hintedReplicaId && replicaId === hintedReplicaId && (!hintedDate || String(event?.date || '') === hintedDate)) {
        changed = true;
        return { ...event, certificationId: hintedId };
      }
      const certification = replicaMap.get(replicaId)?.certification;
      const certId = String(certification?.id || '').trim();
      const certDate = String(certification?.certifiedAt || '').slice(0, 10);
      if (!certId || (certDate && String(event?.date || '') !== certDate)) return event;
      changed = true;
      return { ...event, certificationId: certId };
    });
    return changed ? write(KEYS.timeline, next) : false;
  };
  const enrichKnowledgeRefs = () => {
    const exactChanged = persistPendingExactModel();
    const certChanged = repairCertificationIds();
    return exactChanged || certChanged;
  };

  const copy = async () => {
    const payloadObject = envelope();
    const payload = JSON.stringify(payloadObject);
    let ok = false;
    try {
      await navigator.clipboard.writeText(payload);
      ok = true;
    } catch (_) {
      try {
        const area = document.createElement('textarea');
        area.value = payload;
        area.setAttribute('readonly', '');
        area.style.position = 'fixed';
        area.style.opacity = '0';
        document.body.appendChild(area);
        area.select();
        ok = document.execCommand('copy');
        area.remove();
      } catch (_) { ok = false; }
    }
    window.AWAnalytics?.track?.('p2_cloud_migration_copied', {
      bytes: payload.length,
      schema_version: 3,
      personal_context_active: Boolean(payloadObject.cloudState?.personalContext?.activeReplicaId),
      ok
    });
    return ok;
  };
  const inject = () => {
    const root = document.querySelector('[data-aw-garage-p2]');
    const tools = root?.querySelector('.aw-p2-tools');
    if (!tools || tools.querySelector('[data-aw-p2-cloud-copy]')) return;
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'button button--secondary';
    button.dataset.awP2CloudCopy = 'true';
    button.textContent = 'Copier vers Garage Cloud';
    button.addEventListener('click', async () => {
      const initial = button.textContent;
      const ok = await copy();
      button.textContent = ok ? 'Snapshot Cloud copié ✓' : 'Copie impossible';
      window.setTimeout(() => { button.textContent = initial; }, 2200);
    });
    tools.prepend(button);
  };
  const captureManualImport = (event) => {
    const input = event.target?.closest?.('[data-aw-garage-import]');
    const file = input?.files?.[0];
    if (!file) return;
    file.text().then((text) => {
      let payload = null;
      try { payload = JSON.parse(text); } catch (_) { return; }
      window.setTimeout(() => {
        restoreIncomingModelHandles(payload || {});
        repairCertificationIds();
      }, 0);
    }).catch(() => {});
  };
  const start = () => {
    inject();
    window.setTimeout(() => repairCertificationIds(), 0);
  };

  document.addEventListener('submit', captureReplicaSubmit, true);
  document.addEventListener('change', captureManualImport, true);
  window.addEventListener('aw:p2-garage-updated', () => { enrichKnowledgeRefs(); });
  window.addEventListener('aw:p2-cloud-data', (event) => {
    window.setTimeout(() => {
      restoreIncomingModelHandles(event.detail || {});
      repairCertificationIds();
    }, 0);
  });
  window.addEventListener('aw:p2-workshop-certification', (event) => {
    window.setTimeout(() => repairCertificationIds(event.detail || {}), 0);
  });

  window.AWP2CloudExportBridge = {
    version: '1.3.2',
    envelope,
    copy,
    activeSnapshot,
    inject,
    buildKnowledgeSignals,
    enrichKnowledgeRefs,
    restoreIncomingModelHandles,
    repairCertificationIds,
    policy: 'knowledge-refs-only;sanitized-knowledge-signals;personal-context-explicit-only;no-compatibility-write;no-pii;active-evidence-signals-only;derived-status-not-knowledge'
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
  document.addEventListener('shopify:section:load', start);
})();
